import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { SubmitHandler, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { useOnError } from 'src/utils/errors';
import { ICON_MAP } from 'src/utils/constants';
import { GeneralASError } from 'src/registry/types/axios';
import { cn } from 'src/utils';
import { useGetWebhookEndpoint } from 'src/features/webhook-endpoints/api/useGetWebhookEndpoint';
import {
    WebhookEndpointSchemaValues,
    webhookEndpointFormSchema,
} from 'src/features/webhook-endpoints/utils/formSchema';
import WebhookEndpointForm from 'src/features/webhook-endpoints/components/forms/WebhookEndpointForm';
import { WebhookEndpoint } from 'src/features/webhook-endpoints/utils/types';
import { getDirtyValues } from 'src/utils/getDirtyValues';

interface WebhookEndpointFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedWebhookEndpoint?: WebhookEndpoint;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: WebhookEndpointSchemaValues,
        onSuccess: () => void,
        onError: (error: GeneralASError) => void,
    ) => void;
}

function WebhookEndpointFormModal<TData>({
    selectedWebhookEndpoint,
    isPending,
    children,
    className,
    icon,
    title,
    subtitle,
    mutate,
}: WebhookEndpointFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);

    const { data: webhookEndpoint } = useGetWebhookEndpoint({
        endpointId: selectedWebhookEndpoint?.id ?? '',
        config: { enabled: !!selectedWebhookEndpoint?.id && isModalOpen },
    });

    const onError = useOnError();

    const defaultValues = React.useMemo(() => {
        return {
            url: webhookEndpoint?.url ?? undefined,
            description: webhookEndpoint?.description ?? undefined,
            topics: webhookEndpoint?.topics?.map((topic) => topic) ?? undefined,
            is_active: webhookEndpoint?.is_active ?? true,
        };
    }, [webhookEndpoint]);

    const form = useForm<WebhookEndpointSchemaValues>({
        resolver: zodResolver(webhookEndpointFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [form, isModalOpen, defaultValues]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();
        setIsModalOpen(isOpen);
    };

    const onSubmit: SubmitHandler<WebhookEndpointSchemaValues> = React.useCallback(
        (values: WebhookEndpointSchemaValues) => {
            const dirtyFields = getDirtyValues(form.formState.dirtyFields, values);

            mutate(
                { ...dirtyFields } as WebhookEndpointSchemaValues,
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            );
        },
        [form.setError, mutate, onError],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={cn('ml-auto', className)}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <WebhookEndpointForm form={form} onSubmit={onSubmit} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedWebhookEndpoint && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default WebhookEndpointFormModal;
